import { getValidToken } from '@/lib/auth-token'
import { AI_API_BASE_URL } from '@/lib/config'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** The readable reason in an error body: `error` (our routes), or FastAPI's `detail` (a string or validation list). */
function errorMessage(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return ''
  const { error, detail } = payload as { error?: unknown; detail?: unknown }
  if (error) return String(error)
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: unknown } | undefined
    return first?.msg ? String(first.msg) : ''
  }
  return ''
}

async function request<T>(endpoint: string, method: HttpMethod, body?: unknown): Promise<T> {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  const url = `${AI_API_BASE_URL}${normalizedEndpoint}`
  const token = getValidToken()

  const headers = new Headers()
  headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  const contentType = res.headers.get('content-type')
  const isJson = contentType?.includes('application/json')
  const payload = isJson ? await res.json() : null

  if (!res.ok) {
    throw new Error(errorMessage(payload) || `${res.status} ${res.statusText}`)
  }

  return payload as T
}

export const aiApiClient = {
  get: <T>(endpoint: string) => request<T>(endpoint, 'GET'),
  post: <T>(endpoint: string, data?: unknown) => request<T>(endpoint, 'POST', data),
  put: <T>(endpoint: string, data?: unknown) => request<T>(endpoint, 'PUT', data),
  patch: <T>(endpoint: string, data?: unknown) => request<T>(endpoint, 'PATCH', data),
  delete: <T>(endpoint: string) => request<T>(endpoint, 'DELETE'),
}
